import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import { AdminStarRating, InterestStatsPanel, RatingsBreakdown } from "../../../components/DealFlow";
import { canApprove, isEvaluator } from "../../../utils/dealFlowRoleUtils";
import {
  approveCampaignReview,
  rejectCampaignReview,
  fetchCampaignProducts,
} from "../../../utils/campaignViewActions";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": { borderBottom: 0 },
  "&:before": { display: "none" },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255, 255, 255, .05)" : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

/**
 * ForReviewActions — Draft review workflow
 * Backend: admin/approve-admin-review (raiseCapital.controller.js:5825)
 * Applies all pending draft data and can reclassify APPROVED→COMMING_SOON
 */
const ForReviewActions = ({
  canEdit,
  userInfo,
  campaign,
  campaignId,
  ratingsData,
  myRating,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState("panel1");
  const [previewData, setPreviewData] = useState([]);
  const [productsType, setProductsType] = useState([]);
  const [productId, setProductId] = useState();
  // F-2b (#26): the reason shown to the issuer on a rejection. Named
  // rejectReason, not `comment`, to stay distinct from the issuer's own
  // resubmit note that the preview accordion renders below.
  const [rejectReason, setRejectReason] = useState("");

  const resolvedCampaignId = campaignId || campaign?.data?.[0]?.id;
  const slug = campaign?.data?.[0]?.slug;
  const userId = campaign?.data?.[0]?.user_id;

  useEffect(() => {
    if (slug) previewCampaign();
    if (campaign?.data?.[0]?.type) {
      fetchCampaignProducts(campaign.data[0].type).then(setProductsType);
    }
  }, [campaign]);

  const handleAccordionChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const previewCampaign = () => {
    const token = localStorage.getItem("accessToken");
    axios({
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-campaigndetails-listby-slug",
      headers: { "x-access-token": token },
      data: { slug, user_id: userId, campaign_id: resolvedCampaignId },
    })
      .then((response) => {
        setPreviewData(response.data?.data?.rows || []);
      })
      .catch((error) => {
        console.error("Failed to load preview data", error);
      });
  };

  // QA 2026-07-28 F-1: these two used to raise the page-level loader before
  // awaiting a call that OPENS a confirmation dialog, so the overlay (z 99999)
  // covered the Swal (z 1060) and ate every click on its buttons - the loader
  // could only be lowered by the click it was blocking. `confirmAction` already
  // passes `preConfirm` + `showLoaderOnConfirm`, so SweetAlert renders its own
  // spinner inside the modal for the network phase: the outer loader was
  // redundant as well as harmful. Same ordering the rest of the console uses.
  const handleApprove = async () => {
    const result = await approveCampaignReview(resolvedCampaignId, {
      slug,
      userId,
      productId,
      previewData,
    });
    if (result.success && result.navigateTo) navigate(result.navigateTo);
  };

  const handleReject = async () => {
    // F-2b: the reason travels with the decision. Optional - the API stores
    // NULL when it is blank, and the issuer-facing banner renders nothing
    // rather than an empty heading.
    const result = await rejectCampaignReview(resolvedCampaignId, {
      slug,
      userId,
      comment: rejectReason,
    });
    if (result.success && result.navigateTo) navigate(result.navigateTo);
  };

  return (
    <div className="left">
      <h1 className="title">Campaign Review (Pending Edits)</h1>

      {/* Star Rating */}
      {resolvedCampaignId && (
        <div style={{ marginBottom: "16px" }}>
          <AdminStarRating
            campaignId={Number(resolvedCampaignId)}
            initialRating={myRating?.rating || 0}
            initialNotes={myRating?.notes || ""}
          />
          <RatingsBreakdown ratings={ratingsData} loading={!ratingsData} />
          <InterestStatsPanel
            campaignId={Number(resolvedCampaignId)}
            currency={userInfo?.currency_type || "CAD"}
          />
        </div>
      )}

      {/* Product Type Selection */}
      {productsType.length > 0 && (
        <div className="detailItem" style={{ marginBottom: "12px" }}>
          <span className="itemKey">Product Type:</span>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="itemValue"
          >
            <option value="">Select Product</option>
            {productsType.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.product_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* F-2b (#26) — the reason a rejection is being made.
          Until now this workflow collected NOTHING, so an issuer was told their
          campaign was rejected and never told why, and resubmitted unchanged.
          The value reaches campaign_details.rejection_reason and is rendered
          back to the issuer in My Campaigns and in all three edit surfaces.

          Deliberately labelled "Reason for rejection", not "Comment": the
          accordion further down renders the ISSUER's own resubmit note under a
          bare "Comment:" label, and the two must never be conflated. */}
      {campaign?.data?.[0]?.isForAdminReview === 1 && canApprove() && canEdit !== false && (
        <div className="detailItem" style={{ marginBottom: "12px" }}>
          <span className="itemKey">Reason for rejection:</span>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="itemValue"
            rows={3}
            placeholder="Shown to the issuer if you reject (optional)"
            style={{ width: "100%", padding: "8px" }}
          />
        </div>
      )}

      {/* Approve/Reject — only when campaign is flagged for review */}
      {campaign?.data?.[0]?.isForAdminReview === 1 && canApprove() && canEdit !== false && (
        <div style={{ marginBottom: "16px" }}>
          <p>
            You can click on Approve / Reject button below to respond.
          </p>
          <p>
            <b>Note</b>: Before Approve/Reject campaign please check
            the preview data below.
          </p>
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button className="draftapproveButton" onClick={handleApprove}>
              Approve
            </button>
            <button
              className="draftapproveButton"
              onClick={handleReject}
              style={{ background: "#d33" }}
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {isEvaluator() && (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          As an Evaluator, you can rate campaigns but cannot approve or reject them.
        </p>
      )}

      {/* Draft Preview Accordion */}
      {previewData.length > 0 && (
        <div style={{ marginTop: "16px" }}>
          <h3>Draft Changes Preview</h3>
          {previewData.map((draft, idx) => (
            <Accordion
              key={idx}
              expanded={expanded === `panel${idx + 1}`}
              onChange={handleAccordionChange(`panel${idx + 1}`)}
            >
              <AccordionSummary>
                <Typography>Draft Version {draft.version_number || idx + 1}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography component="div">
                  {/* QA 2026-09-07 (F28): approval applies EVERY step the draft
                      carries, so the reviewer must see every step. Only step
                      one and seven were rendered; personal, bank, team and the
                      rest were invisible. */}
                  {[
                    ["step_one", "Step 1 (Basic Info)"],
                    ["step_two", "Step 2 (Personal / representative)"],
                    ["step_three", "Step 3 (Questionnaire)"],
                    ["step_four", "Step 4 (Banking)"],
                    ["step_five", "Step 5 (About)"],
                    ["step_six", "Step 6 (Team)"],
                    ["step_seven", "Step 7 (Terms / Launch Date)"],
                    ["step_eight", "Step 8 (News)"],
                    ["step_nine", "Step 9 (Media / FAQs)"],
                  ].map(([key, label]) => (
                    draft[key] && Object.keys(draft[key] || {}).length > 0 ? (
                      <div style={{ marginBottom: "8px" }} key={key}>
                        <strong>{label}:</strong>
                        <pre style={{ fontSize: "12px", overflow: "auto" }}>
                          {JSON.stringify(draft[key], null, 2)}
                        </pre>
                      </div>
                    ) : null
                  ))}
                  {/* F-2b: relabelled from a bare "Comment:". This is the
                      ISSUER's note explaining what they changed on resubmission
                      (campaign_drafts.comment, G-4/B-2c) - NOT the admin's
                      reason for rejection, which is the box above and lands in
                      a different column. Two boxes both called "Comment" is how
                      staff would end up typing a rejection reason into the
                      preview and wondering why the issuer never saw it. */}
                  {draft.comment && (
                    <div>
                      <strong>Issuer&apos;s note:</strong> {draft.comment}
                    </div>
                  )}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      )}
    </div>
  );
};

export default ForReviewActions;
